import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

import {
  Campaign,
  CampaignReason,
  CampaignRecipient,
  CampaignResults,
} from "@/common/model/Campaign";
import { CampaignService, parseRecipientCsv } from "@/services/CampaignService";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";

type Tab = "reasons" | "recipients" | "results";

/** Moosend personalization tag that resolves to each recipient's feedback link. */
const WINBACK_LINK_TAG = "#recipient:winback_link#";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * Build the email body HTML: one button per reason, each linking to the recipient's
 * Moosend-personalized feedback link with the reason code appended.
 */
function buildEmailHtml(reasons: CampaignReason[]): string {
  const items = reasons
    .map((r) => `  <li><a href="${WINBACK_LINK_TAG}&amp;code=${encodeURIComponent(r.code)}">${escapeHtml(r.label)}</a></li>`)
    .join("\n");

  return `<!-- #recipient:winback_link# is the Moosend personalization tag that resolves to each recipient's feedback link (e.g. https://api.thesurfr.app/feedback?t=THEIR_TOKEN); each link appends its reason code. The target Moosend list must have a custom field named winback_link. -->
<ul>
${items}
</ul>`;
}

const emptyReason: Partial<CampaignReason> = {
  code: "",
  label: "",
  redirectUrl: "",
  forwardOfferUrl: false,
  sortOrder: 0,
  active: true,
};

function CampaignDetailPage() {
  const { id } = useParams();
  const campaignId = Number(id);
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [tab, setTab] = useState<Tab>("reasons");
  const [syncing, setSyncing] = useState(false);

  const loadCampaign = useCallback(async () => {
    try {
      setCampaign(await CampaignService.get(campaignId));
    } catch (e: any) {
      toast.error(`Failed to load campaign: ${e?.message ?? e}`);
    }
  }, [campaignId]);

  const syncMoosend = async () => {
    setSyncing(true);
    try {
      const r = await CampaignService.syncMoosend(campaignId);
      toast.success(`Moosend → list ${r.list}: ${r.sent} synced, ${r.failed} failed, ${r.skippedNoEmail} skipped (no email)`);
      loadCampaign();
    } catch (e: any) {
      toast.error(`Moosend sync failed: ${e?.response?.data?.error ?? e?.message ?? e}`);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  return (
    <div className="p-6">
      <button className="mb-3 flex items-center text-sm text-muted-foreground hover:underline" onClick={() => navigate("/campaigns")}>
        <ArrowLeftIcon className="mr-1 h-4 w-4" /> All campaigns
      </button>

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{campaign?.name ?? "Campaign"}</h1>
          <p className="text-sm text-muted-foreground">
            {campaign?.status} · {campaign?.recipients ?? 0} recipients · {campaign?.answered ?? 0} answered
          </p>
        </div>
        <div className="text-right">
          <Button size="sm" onClick={syncMoosend} disabled={syncing}>
            {syncing ? "Syncing…" : "Sync to Moosend"}
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">
            {campaign?.lastMoosendSyncAt
              ? `Last synced ${new Date(campaign.lastMoosendSyncAt).toLocaleString()}`
              : "Not synced to Moosend yet"}
          </p>
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-inputboxborder">
        {(["reasons", "recipients", "results"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm capitalize ${
              tab === t ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "reasons" && <ReasonsTab campaignId={campaignId} />}
      {tab === "recipients" && <RecipientsTab campaignId={campaignId} onChanged={loadCampaign} />}
      {tab === "results" && <ResultsTab campaignId={campaignId} />}
    </div>
  );
}

// --------------------------------------------------------------------------- Reasons

function ReasonsTab({ campaignId }: { campaignId: number }) {
  const [reasons, setReasons] = useState<CampaignReason[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<CampaignReason>>(emptyReason);
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const emailHtml = buildEmailHtml(reasons);
  const downloadHtml = () => {
    const blob = new Blob([emailHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "winback-email.html";
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    try {
      setReasons(await CampaignService.listReasons(campaignId));
    } catch (e: any) {
      toast.error(`Failed to load reasons: ${e?.message ?? e}`);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const missing: string[] = [];
    if (!form.code?.trim()) missing.push("Code");
    if (!form.label?.trim()) missing.push("Label");
    if (!form.redirectUrl?.trim()) missing.push("Redirect URL");
    if (missing.length) {
      toast.error(`${missing.join(", ")} ${missing.length > 1 ? "are" : "is"} required`);
      return;
    }
    setSaving(true);
    try {
      if (form.id) await CampaignService.updateReason(form.id, form);
      else await CampaignService.createReason(campaignId, form);
      toast.success("Reason saved");
      setModalOpen(false);
      load();
    } catch (e: any) {
      toast.error(`Save failed: ${e?.response?.data?.error ?? e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: CampaignReason) => {
    if (!window.confirm(`Delete reason "${r.label}"?`)) return;
    try {
      await CampaignService.deleteReason(r.id);
      load();
    } catch (e: any) {
      // 409 when recipients have already answered with this reason.
      toast.error(e?.response?.data?.error ?? `Delete failed: ${e?.message ?? e}`);
    }
  };

  // Move a reason up/down: swap with its neighbour, renumber sort_order 0..n-1,
  // and persist only the rows whose order changed.
  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= reasons.length) return;
    const arr = [...reasons];
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setReasons(arr); // optimistic
    try {
      const changed = arr
        .map((r, i) => ({ r, i }))
        .filter(({ r, i }) => r.sortOrder !== i);
      await Promise.all(changed.map(({ r, i }) => CampaignService.updateReason(r.id, { sortOrder: i })));
      load();
    } catch (e: any) {
      toast.error(`Reorder failed: ${e?.message ?? e}`);
      load();
    }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={reasons.length === 0} onClick={() => setExportOpen(true)}>
          Export email HTML
        </Button>
        <Button size="sm" onClick={() => { setForm({ ...emptyReason, sortOrder: reasons.length }); setModalOpen(true); }}>
          <PlusIcon className="mr-1 h-4 w-4" /> Add reason
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-inputboxborder">
        <table className="w-full text-sm">
          <thead className="bg-backgroundbox text-left">
            <tr>
              <th className="px-2 py-2">Order</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Redirect URL</th>
              <th className="px-3 py-2 text-center">Fwd offers</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reasons.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No reasons yet.</td></tr>
            )}
            {reasons.map((r, i) => (
              <tr key={r.id} className="border-t border-inputboxborder">
                <td className="px-2 py-2">
                  <div className="flex items-center gap-0.5">
                    <button
                      className="rounded p-1 hover:bg-backgroundbox disabled:opacity-30"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                      title="Move up"
                    >
                      <ChevronUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      className="rounded p-1 hover:bg-backgroundbox disabled:opacity-30"
                      disabled={i === reasons.length - 1}
                      onClick={() => move(i, 1)}
                      title="Move down"
                    >
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono">{r.code}</td>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 max-w-xs truncate text-muted-foreground">{r.redirectUrl}</td>
                <td className="px-3 py-2 text-center">{r.forwardOfferUrl ? "✓" : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setForm(r); setModalOpen(true); }}><PencilIcon className="h-4 w-4" /></Button>
                    <Button size="sm" variant="delete" onClick={() => remove(r)}><TrashIcon className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div className="p-6 text-gray-900">
          <h2 className="mb-4 text-lg font-semibold">{form.id ? "Edit reason" : "Add reason"}</h2>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Code (used in the link)</label>
              <Input value={form.code ?? ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. c1" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Label (email button text)</label>
              <Input value={form.label ?? ""} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Redirect URL (landing page)</label>
              <Input value={form.redirectUrl ?? ""} onChange={(e) => setForm({ ...form, redirectUrl: e.target.value })} />
              <p className="mt-1 text-xs text-muted-foreground">
                Placeholders (replaced per recipient, url-encoded): <code>[code]</code> <code>[email]</code>{" "}
                <code>[name]</code>. They’re only substituted, never auto-appended.
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.forwardOfferUrl} onChange={(e) => setForm({ ...form, forwardOfferUrl: e.target.checked })} />
                Forward offer URLs
              </label>
              {form.forwardOfferUrl && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Put <code>[monthly]</code> and <code>[annual]</code> in the Redirect URL above — they’re replaced
                  with this recipient’s offer URLs (url-encoded). e.g.{" "}
                  <code>https://www.thesurfr.app/cancelcode.html?annual=[annual]&amp;monthly=[monthly]</code>
                </p>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={exportOpen} onClose={() => setExportOpen(false)}>
        <div className="p-6 text-gray-900">
          <h2 className="mb-1 text-lg font-semibold">Email body HTML</h2>
          <p className="mb-3 text-sm text-gray-600">
            Paste into Moosend's HTML editor. <code>#recipient:winback_link#</code> is the per-recipient
            personalization tag — make sure the list has a <code>winback_link</code> custom field.
          </p>
          <textarea
            readOnly
            className="h-64 w-full rounded-md border border-inputboxborder bg-white px-3 py-2 font-mono text-xs"
            value={emailHtml}
            onFocus={(e) => e.currentTarget.select()}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigator.clipboard.writeText(emailHtml).then(() => toast.success("HTML copied"))}
            >
              Copy
            </Button>
            <Button variant="outline" onClick={downloadHtml}>Download .html</Button>
            <Button onClick={() => setExportOpen(false)}>Close</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// --------------------------------------------------------------------------- Recipients

function RecipientsTab({ campaignId, onChanged }: { campaignId: number; onChanged: () => void }) {
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setRecipients(await CampaignService.listRecipients(campaignId));
    } catch (e: any) {
      toast.error(`Failed to load recipients: ${e?.message ?? e}`);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  const generate = async () => {
    const { rows, errors } = parseRecipientCsv(csv);
    if (errors.length) toast.warn(`${errors.length} line(s) skipped: ${errors[0]}`);
    if (rows.length === 0) {
      toast.error("No valid recipient rows found");
      return;
    }
    setBusy(true);
    try {
      const res = await CampaignService.upsertRecipients(campaignId, rows);
      toast.success(`Generated: ${res.created} created, ${res.updated} updated, ${res.errors} errors`);
      load();
      onChanged();
    } catch (e: any) {
      toast.error(`Generate failed: ${e?.response?.data?.error ?? e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  };

  const copyBtn = (url?: string | null) =>
    url ? (
      <button className="flex items-center gap-1 text-accent hover:underline" onClick={() => copy(url)} title={url}>
        <ClipboardIcon className="h-4 w-4" /> copy
      </button>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-inputboxborder p-4">
        <label className="mb-1 block text-sm font-medium">Recipients CSV — one <code>userid,monthlyUrl,annualUrl</code> per line</label>
        <textarea
          className="h-36 w-full rounded-md border border-inputboxborder bg-backgroundbox px-3 py-2 font-mono text-xs"
          placeholder={"134,https://apps.apple.com/redeem?m=...,https://apps.apple.com/redeem?a=...\n135,https://...,https://..."}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />
        <div className="mt-2 flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>Upload .csv</Button>
          <Button size="sm" onClick={generate} disabled={busy}>{busy ? "Generating…" : "Generate links"}</Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-inputboxborder">
        <table className="w-full text-sm">
          <thead className="bg-backgroundbox text-left">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Feedback link</th>
              <th className="px-3 py-2">Monthly offer</th>
              <th className="px-3 py-2">Annual offer</th>
              <th className="px-3 py-2">Answer</th>
              <th className="px-3 py-2">Answered at</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No recipients yet.</td></tr>
            )}
            {recipients.map((r) => (
              <tr key={r.id} className="border-t border-inputboxborder">
                <td className="px-3 py-2">
                  <div>{r.userName ?? `#${r.userId}`}</div>
                  <div className="text-xs text-muted-foreground">#{r.userId} {r.username ?? ""}</div>
                </td>
                <td className="px-3 py-2">{copyBtn(r.link)}</td>
                <td className="px-3 py-2">{copyBtn(r.offerUrlMonthly)}</td>
                <td className="px-3 py-2">{copyBtn(r.offerUrlAnnual)}</td>
                <td className="px-3 py-2">
                  {r.reasonLabel ? <span>{r.reasonLabel} <span className="text-muted-foreground">({r.reasonCode})</span></span> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.answeredAt ? new Date(r.answeredAt).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- Results

function ResultsTab({ campaignId }: { campaignId: number }) {
  const [results, setResults] = useState<CampaignResults | null>(null);

  useEffect(() => {
    CampaignService.results(campaignId)
      .then(setResults)
      .catch((e: any) => toast.error(`Failed to load results: ${e?.message ?? e}`));
  }, [campaignId]);

  if (!results) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const { totals, byReason } = results;
  const max = byReason.reduce((m, b) => Math.max(m, b.count), 0) || 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Recipients" value={totals.recipients} />
        <Stat label="Answered" value={totals.answered} />
        <Stat label="Response rate" value={`${Math.round(totals.responseRate * 100)}%`} />
      </div>

      <div className="rounded-lg border border-inputboxborder p-4">
        <h3 className="mb-3 text-sm font-semibold">Answers by reason</h3>
        {byReason.length === 0 && <p className="text-sm text-muted-foreground">No answers yet.</p>}
        <div className="space-y-2">
          {byReason.map((b) => (
            <div key={b.code ?? "unknown"} className="flex items-center gap-3">
              <div className="w-48 truncate text-sm">{b.label ?? b.code ?? "—"}</div>
              <div className="h-4 flex-1 rounded bg-backgroundbox">
                <div className="h-4 rounded bg-accent" style={{ width: `${(100 * b.count) / max}%` }} />
              </div>
              <div className="w-10 text-right text-sm">{b.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-inputboxborder p-4">
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export default CampaignDetailPage;
